/* eslint-disable no-magic-numbers, unicorn/no-null, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/prefer-promise-reject-errors, unicorn/no-useless-promise-resolve-reject, promise/prefer-await-to-callbacks, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unnecessary-type-assertion, sonarjs/no-dead-store, sonar/no-dead-store, @typescript-eslint/prefer-destructuring, unicorn/prevent-abbreviations */

import axios from 'axios';
import type { TMintFileAccessSessionResponse } from '@newo/api/gateway';
import type {
    GCSAxiosError,
    GCSAxiosRequestConfig,
    GCSClientContext,
    GCSClientInitOptions,
    GCSFetchResult,
    GCSGenerationBody,
    GCSListResponse,
    GCSObject,
    GCSResponseHeaders,
    GCSResponseWithHeaders,
    GCSUploadOptions,
    GCSUploadResult
} from './gcsClient.types';

class GCSClient {
    private readonly REFRESH_THRESHOLD_MS = 60 * 1000;
    private context: GCSClientContext | null = null;
    private mintRequestId = 0;
    private pendingMintVersionId: string | null = null;

    beginMint(versionId: string): number {
        this.pendingMintVersionId = versionId;
        this.mintRequestId += 1;
        return this.mintRequestId;
    }

    finishMint(versionId: string, requestId: number): boolean {
        if (
            this.pendingMintVersionId !== versionId ||
            this.mintRequestId !== requestId
        ) {
            return false;
        }
        this.pendingMintVersionId = null;
        return true;
    }

    cancelMint(versionId: string): void {
        if (this.pendingMintVersionId !== versionId) {
            return;
        }
        this.pendingMintVersionId = null;
        this.mintRequestId += 1;
    }

    init(options: GCSClientInitOptions): this {
        this.reset();

        const instance = axios.create({
            baseURL: options.session.api_base_url,
            headers: {
                Authorization: `Bearer ${options.session.access_token}`
            },
            transformResponse: [(data: unknown) => data],
            responseType: 'text'
        });

        const context: GCSClientContext = {
            axios: instance,
            session: options.session,
            onTokenExpired: options.onTokenExpired,
            refreshPromise: null,
            abortController: new AbortController()
        };
        this.context = context;
        this.registerInterceptors(context);
        return this;
    }

    reset(): void {
        this.context?.abortController.abort();
        this.context = null;
        this.pendingMintVersionId = null;
        this.mintRequestId += 1;
    }

    isInitialized(): boolean {
        return this.context !== null;
    }

    getPrefix(): string {
        return this.getContext().session.prefix;
    }

    private getContext(): GCSClientContext {
        if (!this.context) {
            throw new Error('GCS client is not initialized');
        }
        return this.context;
    }

    private isExpiringSoon(context: GCSClientContext): boolean {
        return (
            new Date(context.session.expires_at).getTime() - Date.now() <
            this.REFRESH_THRESHOLD_MS
        );
    }

    private async ensureFreshSession(
        context: GCSClientContext
    ): Promise<TMintFileAccessSessionResponse> {
        if (!context.refreshPromise) {
            context.refreshPromise = context.onTokenExpired().finally(() => {
                context.refreshPromise = null;
            });
        }
        return context.refreshPromise;
    }

    private registerInterceptors(context: GCSClientContext): void {
        context.axios.interceptors.request.use(async (config) => {
            config.signal = context.abortController.signal;
            if (this.isExpiringSoon(context)) {
                try {
                    const newSession = await this.ensureFreshSession(context);
                    context.session = newSession;
                    (config.headers as Record<string, string>).Authorization =
                        `Bearer ${newSession.access_token}`;
                    context.axios.defaults.baseURL = newSession.api_base_url;
                } catch (error) {
                    return Promise.reject(error);
                }
            }
            return config;
        });

        context.axios.interceptors.response.use(
            (response) => response,
            async (error: unknown) => {
                const axiosError = error as GCSAxiosError;
                const status = axiosError?.response?.status;
                const retried = (
                    axiosError?.config as GCSAxiosRequestConfig | undefined
                )?.__gcsRetry;

                if (
                    (status === 401 || status === 403) &&
                    !retried &&
                    axiosError?.config &&
                    this.context === context
                ) {
                    const newSession = await this.ensureFreshSession(context);
                    context.session = newSession;
                    context.axios.defaults.baseURL = newSession.api_base_url;
                    axiosError.config.__gcsRetry = true;
                    (
                        axiosError.config.headers as Record<string, string>
                    ).Authorization = `Bearer ${newSession.access_token}`;
                    return context.axios.request(axiosError.config);
                }

                throw error;
            }
        );
    }

    private resolveObjectName(
        objectName: string,
        session: TMintFileAccessSessionResponse
    ): string {
        const name = objectName.replace(/^\//, '');
        const prefix = session.prefix.replace(/^\//, '');
        if (!prefix || name.startsWith(prefix)) {
            return name;
        }
        return `${prefix.replace(/\/$/, '')}/${name}`;
    }

    private resolvePrefix(
        session: TMintFileAccessSessionResponse,
        prefix?: string
    ): string {
        if (prefix === undefined) {
            return session.prefix;
        }
        return this.resolveObjectName(prefix, session);
    }

    async listObjects(prefix?: string): Promise<GCSListResponse> {
        const context = this.getContext();
        const effectivePrefix = this.resolvePrefix(context.session, prefix);
        let pageToken: string | undefined;
        const allItems: GCSObject[] = [];
        let kind = 'storage#objects';

        do {
            const response = await context.axios.get(
                `/storage/v1/b/${context.session.bucket}/o`,
                {
                    params: { prefix: effectivePrefix, pageToken },

                    transformResponse: axios.defaults.transformResponse,
                    responseType: 'json'
                }
            );
            const page = response.data as GCSListResponse;
            kind = page.kind;
            if (page.items) {
                allItems.push(...page.items);
            }
            pageToken = page.nextPageToken;
        } while (pageToken);

        return { kind, items: allItems };
    }

    private readGeneration(response: GCSResponseWithHeaders): string | null {
        const headers = response.headers as GCSResponseHeaders;
        if (!headers) {
            return null;
        }
        const raw = headers['x-goog-generation'];
        if (typeof raw === 'string' && raw.length > 0) {
            return raw;
        }
        if (typeof raw === 'number') {
            return String(raw);
        }
        if (
            Array.isArray(raw) &&
            typeof raw[0] === 'string' &&
            raw[0].length > 0
        ) {
            return raw[0];
        }
        return null;
    }

    private extractGenerationFromBody(data: unknown): string | null {
        let parsed: unknown = data;
        if (typeof parsed === 'string') {
            if (parsed.length === 0) {
                return null;
            }
            try {
                parsed = JSON.parse(parsed);
            } catch {
                return null;
            }
        }
        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }
        const value = (parsed as GCSGenerationBody).generation;
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
        if (typeof value === 'number') {
            return String(value);
        }
        return null;
    }

    async getObject(objectName: string): Promise<GCSFetchResult> {
        const context = this.getContext();
        const resolved = this.resolveObjectName(objectName, context.session);
        const url = `/storage/v1/b/${context.session.bucket}/o/${encodeURIComponent(resolved)}`;

        const [bodyResp, metaResp] = await Promise.all([
            context.axios.get<string>(url, { params: { alt: 'media' } }),
            context.axios
                .get(url, {
                    transformResponse: axios.defaults.transformResponse,
                    responseType: 'json'
                })
                .catch(() => null)
        ]);

        const content =
            typeof bodyResp.data === 'string'
                ? bodyResp.data
                : String(bodyResp.data ?? '');

        let generation: string | null = null;
        if (metaResp) {
            generation = this.extractGenerationFromBody(metaResp.data);
        }
        if (generation === null) {
            generation = this.readGeneration(bodyResp);
        }
        return { content, generation };
    }

    async uploadObject(
        objectName: string,
        content: string | Blob,
        options: GCSUploadOptions = {}
    ): Promise<GCSUploadResult> {
        const context = this.getContext();
        const resolved = this.resolveObjectName(objectName, context.session);
        const params: Record<string, unknown> = {
            uploadType: 'media',
            name: resolved
        };
        if (
            options.ifGenerationMatch !== undefined &&
            options.ifGenerationMatch !== null
        ) {
            params.ifGenerationMatch = options.ifGenerationMatch;
        }
        const response = await context.axios.post(
            `/upload/storage/v1/b/${context.session.bucket}/o`,
            content,
            {
                params,
                headers: {
                    'Content-Type': options.contentType ?? 'text/plain'
                }
            }
        );

        const generation =
            this.extractGenerationFromBody(response.data) ??
            this.readGeneration(response);
        return { generation };
    }

    async deleteObject(objectName: string): Promise<void> {
        const context = this.getContext();
        const resolved = this.resolveObjectName(objectName, context.session);

        await context.axios.delete(
            `/storage/v1/b/${context.session.bucket}/o/${encodeURIComponent(resolved)}`
        );
    }

    async copyObject(sourceName: string, destName: string): Promise<void> {
        const context = this.getContext();
        const resolvedSrc = this.resolveObjectName(sourceName, context.session);
        const resolvedDest = this.resolveObjectName(destName, context.session);
        const url = `/storage/v1/b/${context.session.bucket}/o/${encodeURIComponent(resolvedSrc)}/copyTo/b/${context.session.bucket}/o/${encodeURIComponent(resolvedDest)}`;
        await context.axios.post(
            url,
            {},
            {
                headers: { 'Content-Type': 'application/json' }
            }
        );
    }
}

export const gcsClient = new GCSClient();
